// ===================================================
// ФАЙЛ: frontend/src/store/authSlice.js
// ✅ ИСПРАВЛЕНО: Приведено в соответствие с бэкендом
// ===================================================
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { message } from 'antd';
import axios from 'utils/axios';
import { API_ENDPOINTS } from 'utils/constants';
import { pickSafeRussianBackendMessage } from 'utils/safeRussianBackendMessage';
import { apiRejectPayload } from 'utils/apiRejectPayload';
import { setClientAuthReady, reportSessionUnauthorized } from '../utils/sessionGuard';

// =====================================
// HELPERS
// =====================================

/**
 * Извлекает captcha-поля из объекта данных.
 * Единая точка — без дублирования в каждом thunk.
 */
function extractCaptchaFields(data) {
  const fields = {};
  if (data.captcha_token)  fields.captcha_token  = data.captcha_token;
  if (data.challenge_id)   fields.challenge_id   = data.challenge_id;
  if (data.captcha_answer) fields.captcha_answer = data.captcha_answer;
  if (data.captcha_site_key) fields.captcha_site_key = data.captcha_site_key;
  if (data.captcha_provider) fields.captcha_provider = data.captcha_provider;
  return fields;
}

/** После нового входа/регистрации/гостя — сбрасываем mb_screen_locked, чтобы не показывать прошлую «блокировку» */
function clearScreenLockFlagAfterFreshSession() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('mb_screen_locked', 'false');
    localStorage.removeItem('mb_lock_reason');
    window.dispatchEvent(new Event('markbase_screen_lock_clear_stale'));
  } catch { /* */ }
}

/** Shell-виджеты (согласия, избранное, screen-lock) подписываются и не бьют в API до готовности сессии. */
function emitAuthReady(isAuthenticated) {
  if (typeof window === 'undefined') return;
  try {
    setClientAuthReady(!!isAuthenticated);
  } catch { /* */ }
  try {
    window.dispatchEvent(new CustomEvent('markbase_auth_ready', {
      detail: { isAuthenticated: !!isAuthenticated },
    }));
  } catch { /* */ }
}

// =====================================
// ASYNC THUNKS
// =====================================

/**
 * Проверка авторизации при загрузке приложения
 */
const isResolvedCompany = (company) => company === null || (
  company !== null && typeof company === 'object' && !Array.isArray(company)
  && ((typeof company.id === 'string' && company.id.trim().length > 0)
    || (Number.isInteger(company.id) && company.id > 0))
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue, getState, requestId }) => {
    const isCurrentAuthRequest = () => getState()?.auth?.authRequestId === requestId;
    try {
      const response = await axios.get(API_ENDPOINTS.ME, {
        cacheTtlMs: 0, noDedup: true,
        errorPresentation: 'inline',
        isCurrentAuthRequest,
      });
      const result = response.data;
      if (
        result?.success === true
        && result.data?.user?.id
        && Object.prototype.hasOwnProperty.call(result.data, 'company')
        && isResolvedCompany(result.data.company)
      ) {
        return { isAuthenticated: true, user: result.data.user, company: result.data.company };
      }
      if (result?.success === false) return { isAuthenticated: false };
      return rejectWithValue({
        code: 'AUTH_CONTEXT_UNAVAILABLE',
        message: 'Не удалось загрузить рабочее пространство. Повторите попытку.',
      });
    } catch (error) {
      if (error.response?.status === 401) {
        if (isCurrentAuthRequest() && getState()?.auth?.isAuthenticated) {
          reportSessionUnauthorized({ url: API_ENDPOINTS.ME, status: 401, code: 'AUTH_REQUIRED' });
        }
        return rejectWithValue({ code: 'AUTH_REQUIRED', message: 'Войдите в аккаунт, чтобы продолжить.' });
      }
      return rejectWithValue({
        code: 'AUTH_CONTEXT_UNAVAILABLE',
        message: 'Не удалось загрузить рабочее пространство. Проверьте соединение и повторите попытку.',
      });
    }
  }
);

/**
 * Legacy app registration is fail-closed.
 * Product app must not POST /register itself because UAM Register verifies accepted_consent_types.
 */
export const registerUser = createAsyncThunk(
  'auth/register',
  async (_userData, { rejectWithValue }) => {
    const uamUrl = getUamApiUrl();
    const redirectUrl = `${uamUrl}/register`;

    if (typeof window !== 'undefined') {
      const returnUrl = `${window.location.origin}/auth/callback?to=${encodeURIComponent('/dashboard')}`;
      const target = `${redirectUrl}?return_url=${encodeURIComponent(returnUrl)}`;
      message.info('Регистрация выполняется только через Маркбэйс id с обязательными согласиями.');
      window.location.href = target;
    }

    return rejectWithValue({
      message: 'Регистрация выполняется только через Маркбэйс id с обязательными согласиями.',
      code: 'UAM_REGISTRATION_REQUIRED',
      redirect_url: redirectUrl,
    });
  }
);

/**
 * Вход через Маркбэйс id (UAM)
 * POST напрямую в UAM API, cookie ставится на .markbase.ru
 * После успешного входа подтягиваем /api/auth/me для получения полных данных
 */
const getUamApiUrl = () => {
  const raw = process.env.REACT_APP_UAM_URL || 'https://auth.markbase.ru';
  return raw.replace(/\/+$/, '');
};

export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe, captcha_token, challenge_id, captcha_answer, captcha_site_key, captcha_provider }, { rejectWithValue, getState, requestId }) => {
    try {
      const captchaFields = extractCaptchaFields({ captcha_token, challenge_id, captcha_answer, captcha_site_key, captcha_provider });

      // POST напрямую в UAM API
      const uamUrl = getUamApiUrl();
      const uamResponse = await axios.post(`${uamUrl}/api/uam/v1/login`, {
        email,
        password,
        ...captchaFields
      }, { withCredentials: true });

      const uamResult = uamResponse.data;
      if (!uamResult.success) {
        throw new Error(uamResult.error || 'Не удалось войти');
      }

      // Сохраняем только presentation metadata; credential быстрого входа остаётся HttpOnly.
      const { saveLastLogin } = require('../utils/wsidAccounts');
      saveLastLogin(
        uamResult.user?.email,
        uamResult.user?.display_name,
        {
          quick_login: uamResult.quick_login === true,
          require_password_on_switch: uamResult.require_password_on_switch,
          switch_inactivity_days: uamResult.switch_inactivity_days
        }
      );

      // Cookie uam_session уже установлена на .markbase.ru
      // Подтягиваем полные данные через app backend /api/auth/me
      // ✅ ИСПРАВЛЕНО: оборачиваем /me в try-catch, чтобы ошибка /me
      //    не ломала весь вход (cookie уже установлена, сессия активна)
      let meData = null;
      try {
        const meResp = await axios.get(API_ENDPOINTS.ME, {
          cacheTtlMs: 0, noDedup: true,
          errorPresentation: 'inline',
          isCurrentAuthRequest: () => getState()?.auth?.authRequestId === requestId,
        });
        meData = meResp?.data;
      } catch (meError) {
        if (meError.response?.status === 401) {
          return rejectWithValue({ code: 'AUTH_REQUIRED', message: 'Войдите в аккаунт, чтобы продолжить.' });
        }
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'Не удалось запросить /me после входа через UAM, используем данные UAM:',
            meError.response?.status ?? apiRejectPayload(meError).message,
          );
        }
      }

      if (meData?.success === true && meData.data?.user?.id && Object.prototype.hasOwnProperty.call(meData.data, 'company') && isResolvedCompany(meData.data.company)) {
        const user = meData.data?.user;
        const company = meData.data?.company;
        message.success('Вход выполнен успешно!');
        clearScreenLockFlagAfterFreshSession();
        return { user, company, bootstrapStatus: 'ready' };
      }

      // UAM confirmed identity; a failed workspace lookup is not an empty company list.
      clearScreenLockFlagAfterFreshSession();
      return {
        user: {
          id: uamResult.user?.user_id,
          email: uamResult.user?.email,
          name: uamResult.user?.display_name,
        },
        company: null,
        bootstrapStatus: 'error',
        bootstrapError: 'Вход подтверждён. Не удалось загрузить рабочее пространство. Повторите попытку.',
      };
    } catch (error) {
      const errorData = error.response?.data || {};
      const safe = apiRejectPayload(error, 'Ошибка входа в систему').message;
      const safeDetail = pickSafeRussianBackendMessage(errorData.message, '');
      return rejectWithValue({
        message: safe,
        detailedMessage: safeDetail || undefined,
        captcha_required: errorData.captcha_required === true,
        code: errorData.code
      });
    }
  }
);

/**
 * Выход из системы
 * 1. Вызываем локальный backend /api/auth/logout (очищает cookies + вызывает UAM logout серверно)
 * 2. Best-effort вызываем UAM logout напрямую (гарантирует удаление cookie uam_session)
 * 3. Очищаем localStorage (legacy)
 * 4. Redirect определяется вызывающим кодом (по умолчанию /login)
 *
 * @param {Object} options - { skipRedirect?: boolean }
 *   skipRedirect=true — вызывающий код сам делает redirect (напр. переключение аккаунтов)
 */
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (options = {}, { rejectWithValue }) => {
    const { skipRedirect = false } = options || {};
    const uamUrl = getUamApiUrl();
    try {
      // 1. Локальный backend logout (очистит cookies + дёрнет UAM серверно)
      await axios.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.warn('Вызов API выхода не удался:', error);
    }

    // 2. Belt-and-suspenders: ВСЕГДА вызываем UAM logout напрямую
    //    Даже если backend уже дёрнул UAM — прямой вызов из браузера
    //    гарантирует удаление cookie uam_session на .markbase.ru
    if (typeof window !== 'undefined') {
      try {
        await fetch(`${uamUrl}/api/uam/v1/logout`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (_) {}
    }

    // 3. Очищаем localStorage (legacy)
    try { axios.clearTokens(); } catch (_) {}
    message.success('Вы успешно вышли из системы');

    // 4. Redirect — по умолчанию на /login, если не skipRedirect
    //    skipRedirect=true используется при переключении аккаунтов
    //    (вызывающий код сам делает redirect с ?switch= или ?new_account=1)
    if (!skipRedirect && typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }
);

/**
 * Гостевой вход (без пароля, тестовые данные)
 */
export const guestLogin = createAsyncThunk(
  'auth/guestLogin',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.GUEST_LOGIN, {}, { withCredentials: true });
      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'Не удалось выполнить гостевой вход');
      }

      message.success('Добро пожаловать! Вы вошли как гость с тестовыми данными.');
      clearScreenLockFlagAfterFreshSession();
      return {
        user: result.data?.user,
        company: result.data?.company
      };
    } catch (error) {
      const safe = apiRejectPayload(error, 'Ошибка гостевого входа').message;
      message.error(safe);
      return rejectWithValue({ message: safe });
    }
  }
);

/**
 * Смена пароля
 */
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword, captcha_token, challenge_id, captcha_answer }, { rejectWithValue }) => {
    try {
      const captchaFields = extractCaptchaFields({ captcha_token, challenge_id, captcha_answer });

      const response = await axios.post(API_ENDPOINTS.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
        ...captchaFields
      });
      const result = response.data;

      if (result.success) {
        message.success('Пароль успешно изменен');
        return true;
      }

      throw new Error(result.error || 'Не удалось сменить пароль');
    } catch (error) {
      const safe = apiRejectPayload(error, 'Ошибка смены пароля').message;
      message.error(safe);
      return rejectWithValue(safe);
    }
  }
);

/**
 * Восстановление пароля (запрос письма)
 */
export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email, captcha_token, challenge_id, captcha_answer }, { rejectWithValue }) => {
    try {
      const captchaFields = extractCaptchaFields({ captcha_token, challenge_id, captcha_answer });

      const response = await axios.post(API_ENDPOINTS.FORGOT_PASSWORD, {
        email,
        ...captchaFields
      });
      const result = response.data;

      if (result.success) {
        message.success('Если такой email существует, ссылка для сброса отправлена');
        return true;
      }

      throw new Error(result.error || 'Не удалось отправить запрос на сброс пароля');
    } catch (error) {
      const safe = apiRejectPayload(error, 'Ошибка запроса на сброс пароля').message;
      message.error(safe);
      return rejectWithValue(safe);
    }
  }
);

/**
 * Сброс пароля по токену
 */
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.RESET_PASSWORD, { token, newPassword });
      const result = response.data;

      if (result.success) {
        message.success('Пароль успешно сброшен');
        return true;
      }

      throw new Error(result.error || 'Не удалось сбросить пароль');
    } catch (error) {
      const safe = apiRejectPayload(error, 'Ошибка сброса пароля').message;
      message.error(safe);
      return rejectWithValue(safe);
    }
  }
);

// =====================================
// INITIAL STATE
// =====================================
// UAM-based auth: сессия хранится в httpOnly cookie, не в localStorage.
// При загрузке всегда проверяем /api/auth/me (cookie отправится автоматически).
const initialState = {
  isAuthenticated: false,
  user: null,
  company: null,
  token: null,
  refreshToken: null,
  loading: false,
  error: null,
  errorCode: null,
  remainingAttempts: null,
  // Всегда проверяем авторизацию при загрузке (cookie может быть)
  checkingAuth: true,
  bootstrapStatus: 'loading',
  bootstrapError: null,
  authRequestId: null,
};

// =====================================
// SLICE
// =====================================
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
      state.errorCode = null;
      state.remainingAttempts = null;
    },

    // Обновление данных пользователя
    updateUser: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    // Полная замена данных пользователя
    setUser: (state, action) => {
      state.user = action.payload;
    },

    // Установка компании
    setCompany: (state, action) => {
      state.company = action.payload;
    },

    // ✅ НОВОЕ: Сброс состояния проверки авторизации (для публичного сайта)
    skipAuthCheck: (state) => {
      state.checkingAuth = false;
      state.loading = false;
      state.isAuthenticated = false;
      state.bootstrapStatus = 'idle';
      state.bootstrapError = null;
      state.authRequestId = null;
    },

    /** Локальный сброс сессии (cookie уже мертва / 401) — без «Вы успешно вышли». */
    clearSessionLocal: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.company = null;
      state.token = null;
      state.refreshToken = null;
      state.checkingAuth = false;
      state.loading = false;
      state.error = null;
      state.bootstrapStatus = 'idle';
      state.bootstrapError = null;
      state.authRequestId = null;
      emitAuthReady(false);
    },
  },
  extraReducers: (builder) => {
    // The request identity also prevents an older account lookup from replacing a new login.
    builder
      .addCase(checkAuth.pending, (state, action) => {
        state.authRequestId = action.meta.requestId;
        state.checkingAuth = true;
        state.bootstrapError = null;
        if (state.bootstrapStatus !== 'ready') state.bootstrapStatus = 'loading';
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.isAuthenticated = action.payload.isAuthenticated;
        state.user = action.payload.isAuthenticated ? action.payload.user : null;
        state.company = action.payload.isAuthenticated ? action.payload.company : null;
        state.token = null;
        state.refreshToken = null;
        state.checkingAuth = false;
        state.bootstrapStatus = action.payload.isAuthenticated ? 'ready' : 'idle';
        state.bootstrapError = null;
        emitAuthReady(action.payload.isAuthenticated);
      })
      .addCase(checkAuth.rejected, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.checkingAuth = false;
        state.token = null;
        state.refreshToken = null;
        if (action.payload?.code === 'AUTH_REQUIRED') {
          state.isAuthenticated = false;
          state.user = null;
          state.company = null;
          state.bootstrapStatus = 'idle';
          state.bootstrapError = null;
        } else {
          state.bootstrapStatus = 'error';
          state.bootstrapError = action.payload?.message || 'Не удалось загрузить рабочее пространство. Повторите попытку.';
        }
        emitAuthReady(false);
      });

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.company = action.payload.company;
        // ✅ UAM-режим: токены хранятся в httpOnly cookie, не в localStorage
        state.token = action.payload.token || null;
        state.refreshToken = action.payload.refreshToken || null;
        state.loading = false;
        state.error = null;
        emitAuthReady(true);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Login
    builder
      .addCase(loginUser.pending, (state, action) => {
        state.authRequestId = action.meta.requestId;
        state.loading = true;
        state.bootstrapStatus = 'loading';
        state.bootstrapError = null;
        state.error = null;
        emitAuthReady(false);
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.checkingAuth = false;
        state.bootstrapStatus = action.payload.bootstrapStatus;
        state.bootstrapError = action.payload.bootstrapError || null;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.company = action.payload.company;
        // ✅ UAM-режим: токены хранятся в httpOnly cookie, не в localStorage
        state.token = action.payload.token || null;
        state.refreshToken = action.payload.refreshToken || null;
        state.loading = false;
        state.error = null;
        state.errorCode = null; // ✅ Очищаем код ошибки
        state.remainingAttempts = null; // ✅ Очищаем попытки
        emitAuthReady(state.bootstrapStatus === 'ready');
      })
      .addCase(loginUser.rejected, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.checkingAuth = false;
        state.bootstrapStatus = 'idle';
        state.bootstrapError = null;
        state.isAuthenticated = false;
        state.user = null;
        state.company = null;
        state.loading = false;
        // ✅ Обрабатываем детальную ошибку с кодом и попытками
        const errorPayload = action.payload;
        if (typeof errorPayload === 'object' && errorPayload.message) {
          state.error = errorPayload.detailedMessage || errorPayload.message;
          state.errorCode = errorPayload.code;
          state.remainingAttempts = errorPayload.remainingAttempts;
        } else {
          state.error = errorPayload || 'Ошибка входа в систему';
        }
      });

    // Guest Login
    builder
      .addCase(guestLogin.pending, (state, action) => {
        state.authRequestId = action.meta.requestId;
        state.bootstrapStatus = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(guestLogin.fulfilled, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.checkingAuth = false;
        state.bootstrapStatus = 'ready';
        state.bootstrapError = null;
        state.isAuthenticated = true;
        emitAuthReady(true);
        state.user = action.payload.user;
        state.company = action.payload.company;
        state.token = null;
        state.refreshToken = null;
        state.loading = false;
        state.error = null;
        state.errorCode = null;
        state.remainingAttempts = null;
      })
      .addCase(guestLogin.rejected, (state, action) => {
        if (state.authRequestId !== action.meta.requestId) return;
        state.authRequestId = null;
        state.bootstrapStatus = 'idle';
        state.loading = false;
        const errorPayload = action.payload;
        if (typeof errorPayload === 'object' && errorPayload.message) {
          state.error = errorPayload.message;
        } else {
          state.error = errorPayload || 'Ошибка гостевого входа';
        }
      });

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.loading = false;
        state.authRequestId = null;
        state.bootstrapStatus = 'idle';
        state.bootstrapError = null;
        state.checkingAuth = false;
        emitAuthReady(false);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.authRequestId = null;
        state.bootstrapStatus = 'idle';
        state.bootstrapError = null;
        state.isAuthenticated = false;
        state.user = null;
        state.company = null;
        state.token = null;
        state.refreshToken = null;
        state.error = null;
      });

    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Forgot Password
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Reset Password
    builder
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// =====================================
// ACTIONS
// =====================================
export const { clearError, updateUser, setUser, setCompany, skipAuthCheck, clearSessionLocal } = authSlice.actions;

// =====================================
// SELECTORS
// =====================================
export const selectUser = (state) => state.auth.user;
export const selectCompany = (state) => state.auth.company;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserRole = (state) => state.auth.user?.role;
export const selectIsActive = (state) => state.auth.user?.is_active === true;
export const selectCheckingAuth = (state) => state.auth.checkingAuth;
export const selectAuthBootstrapStatus = (state) => state.auth.bootstrapStatus;
export const selectAuthBootstrapError = (state) => state.auth.bootstrapError;
export const selectAuthContextReady = (state) => state.auth.isAuthenticated && state.auth.bootstrapStatus === 'ready';
export const selectToken = (state) => state.auth.token;
export const selectRefreshToken = (state) => state.auth.refreshToken;

// =====================================
// EXPORTS
// =====================================
export default authSlice.reducer;
